import { Skeleton, Text } from '@dixa/lib-shared-habla';
import { colors, typography } from '@dixa/lib-shared-tokens';
import { MetricInstrumentData } from 'lib-analytics-ui';
import { PropsWithClassName } from 'lib-shared-types-utils';
import { Fragment, memo, useMemo } from 'react';

import { useChannelDashboardChannelContext } from '../../../../channelDashboardChannel.context';
import { ChannelDashboardMetric } from '../../../metric/channelDashboardMetric';

import styles from './channelDailyConversationsMetric.module.scss';

type ChannelDashboardDailyConversationsMetricProps = PropsWithClassName;

export const ChannelDashboardDailyConversationsMetric = memo(
  function ChannelDashboardDailyConversationsMetric({
    className,
  }: ChannelDashboardDailyConversationsMetricProps): JSX.Element {
    const { metrics, isSkeleton } = useChannelDashboardChannelContext();

    const {
      conversations_answered = 0,
      conversations_received = 0,
      conversations_abandoned,
    } = metrics ?? {};

    const noDataColor =
      !conversations_answered &&
      !conversations_received &&
      !conversations_abandoned
        ? typography.color.tertiary
        : undefined;

    const items = useMemo(
      (): { title: string; value: number | null; color?: string }[] => [
        {
          title: 'Answered',
          value: conversations_answered,
          color: noDataColor ?? colors.green[500],
        },
        ...(typeof conversations_abandoned !== 'undefined'
          ? [
              {
                title: 'Abandoned',
                value: conversations_abandoned,
                color: noDataColor ?? colors.red[500],
              },
            ]
          : []),
        {
          title: 'Received',
          value: conversations_received,
          color: noDataColor,
        },
      ],
      [
        conversations_abandoned,
        conversations_answered,
        conversations_received,
        noDataColor,
      ],
    );

    return (
      <ChannelDashboardMetric
        className={className}
        title="Daily stats"
        isSkeleton={isSkeleton}
        metricType="daily_conversations"
      >
        <div className={styles.content}>
          {items.map(({ title, value, color }) => (
            <Fragment key={title}>
              {isSkeleton ? (
                <Skeleton height="24px" width="32px" />
              ) : (
                <MetricInstrumentData
                  className={styles.value}
                  data={value}
                  color={color}
                  format="count"
                />
              )}

              {isSkeleton ? (
                <Skeleton height="16px" width="72px" />
              ) : (
                <Text.Paragraph as="span" variant="4" subdued>
                  {title}
                </Text.Paragraph>
              )}
            </Fragment>
          ))}
        </div>
      </ChannelDashboardMetric>
    );
  },
);
